import type { UIMessage } from "ai";
import type { ReviewCanvasDocument } from "../components/ReviewResultsCanvas";
import type { ReviewRow, RiskLevel } from "@/types";

const TOOL_NAMES = new Set([
  "start-contract-review",
  "startContractReview",
  "start_contract_review",
]);

function isRisk(value: unknown): value is RiskLevel {
  return value === "acceptable" || value === "acceptable-clarify" || value === "not-acceptable";
}

function asRow(value: unknown): ReviewRow | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const section =
    typeof row.section === "string"
      ? row.section
      : typeof row.section === "number"
        ? String(row.section)
        : "";
  if (!section.trim()) return null;
  const pageNumber =
    typeof row.pageNumber === "number" && Number.isFinite(row.pageNumber)
      ? row.pageNumber
      : typeof row.pageNumber === "string" && row.pageNumber.trim() && Number.isFinite(Number(row.pageNumber))
        ? Number(row.pageNumber)
        : 1;
  const riskLevel = isRisk(row.riskLevel) ? row.riskLevel : "acceptable";
  return {
    section,
    pageNumber,
    sectionLink: typeof row.sectionLink === "string" ? row.sectionLink : "",
    modifiedBy: typeof row.modifiedBy === "string" ? row.modifiedBy : undefined,
    modifiedWhen: typeof row.modifiedWhen === "string" ? row.modifiedWhen : undefined,
    changeRationale: typeof row.changeRationale === "string" ? row.changeRationale : undefined,
    riskLevel,
    riskReasoning: typeof row.riskReasoning === "string" ? row.riskReasoning : "",
    suggestedAction: typeof row.suggestedAction === "string" ? row.suggestedAction : "",
    actionRationale: typeof row.actionRationale === "string" ? row.actionRationale : "",
    precedent: typeof row.precedent === "string" ? row.precedent : undefined,
    suggestedWording: typeof row.suggestedWording === "string" ? row.suggestedWording : "",
    suggestedComment: typeof row.suggestedComment === "string" ? row.suggestedComment : "",
  };
}

function nestedOutput(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (record.reviewTable || record.reviewId || record.success === false) return record;
  if (record.result && typeof record.result === "object") return record.result as Record<string, unknown>;
  return record;
}

function asDocument(value: unknown): ReviewCanvasDocument | null {
  const nested = nestedOutput(value);
  if (!nested || nested.success === false) return null;
  if (!Array.isArray(nested.reviewTable) || typeof nested.reviewId !== "string" || !nested.reviewId) return null;
  const reviewTable = nested.reviewTable.map(asRow).filter((row): row is ReviewRow => row !== null);
  if (reviewTable.length === 0) return null;
  return {
    reviewId: nested.reviewId,
    fileName: typeof nested.fileName === "string" ? nested.fileName : "Contract review",
    summary: typeof nested.summary === "string" ? nested.summary : "",
    overallRiskScore: typeof nested.overallRiskScore === "number" ? nested.overallRiskScore : 0,
    recommendations: Array.isArray(nested.recommendations)
      ? nested.recommendations.filter((item): item is string => typeof item === "string")
      : [],
    reviewTable,
  };
}

function reviewIdFrom(value: unknown): string | null {
  const nested = nestedOutput(value);
  if (!nested || nested.success === false) return null;
  return typeof nested.reviewId === "string" && nested.reviewId ? nested.reviewId : null;
}

export type ReviewCanvasTarget = {
  document: ReviewCanvasDocument | null;
  reviewId: string | null;
};

export type ReviewApiRow = {
  id: string;
  file_name?: string | null;
  summary?: string | null;
  overall_risk_score?: number | null;
  recommendations?: unknown;
  review_table?: unknown;
  contract_type?: string | null;
  jurisdiction?: string | null;
  user_role?: string | null;
  skip_research?: boolean | null;
  background_info?: string | null;
  agent_instructions?: string | null;
};

export function reviewConfigFromApi(review: ReviewApiRow): Pick<
  ReviewCanvasDocument,
  | "contractType"
  | "jurisdiction"
  | "userRole"
  | "skipResearch"
  | "backgroundInfo"
  | "agentInstructions"
> {
  return {
    contractType: typeof review.contract_type === "string" ? review.contract_type : null,
    jurisdiction: typeof review.jurisdiction === "string" ? review.jurisdiction : null,
    userRole: typeof review.user_role === "string" ? review.user_role : null,
    skipResearch: typeof review.skip_research === "boolean" ? review.skip_research : null,
    backgroundInfo: typeof review.background_info === "string" ? review.background_info : null,
    agentInstructions:
      typeof review.agent_instructions === "string" ? review.agent_instructions : null,
  };
}

export function mergeReviewConfig(
  document: ReviewCanvasDocument,
  config: ReturnType<typeof reviewConfigFromApi>
): ReviewCanvasDocument {
  return { ...document, ...config };
}

export function canvasDocumentFromReview(review: ReviewApiRow): ReviewCanvasDocument | null {
  if (!Array.isArray(review.review_table)) return null;
  const reviewTable = review.review_table.map(asRow).filter((row): row is ReviewRow => row !== null);
  if (reviewTable.length === 0) return null;
  return {
    reviewId: review.id,
    fileName: review.file_name || "Contract review",
    summary: typeof review.summary === "string" ? review.summary : "",
    overallRiskScore: typeof review.overall_risk_score === "number" ? review.overall_risk_score : 0,
    recommendations: Array.isArray(review.recommendations)
      ? review.recommendations.filter((item): item is string => typeof item === "string")
      : [],
    reviewTable,
    ...reviewConfigFromApi(review),
  };
}

function toolOutput(part: Record<string, unknown>): unknown {
  if ("output" in part && part.output != null) return part.output;
  if ("result" in part && part.result != null) return part.result;
  return null;
}

function reviewTargetFromPart(part: unknown): ReviewCanvasTarget | null {
  const record = part as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : "";
  const toolName =
    typeof record.toolName === "string"
      ? record.toolName
      : type.startsWith("tool-")
        ? type.slice("tool-".length)
        : "";
  if (!TOOL_NAMES.has(toolName) && !TOOL_NAMES.has(type)) return null;
  const output = toolOutput(record);
  const document = asDocument(output);
  const reviewId = document?.reviewId ?? reviewIdFrom(output);
  if (!document && !reviewId) return null;
  return { document, reviewId };
}

export function reviewIdInMessage(message: UIMessage): string | null {
  if (!Array.isArray(message.parts)) return null;
  let reviewId: string | null = null;
  for (const part of message.parts) {
    const target = reviewTargetFromPart(part);
    if (target?.reviewId) reviewId = target.reviewId;
    else if (target?.document?.reviewId) reviewId = target.document.reviewId;
  }
  return reviewId;
}

export function latestReviewCanvasTarget(messages: UIMessage[]): ReviewCanvasTarget | null {
  let latest: ReviewCanvasTarget | null = null;
  for (const message of messages) {
    if (!Array.isArray(message.parts)) continue;
    for (const part of message.parts) {
      const target = reviewTargetFromPart(part);
      if (target) latest = target;
    }
  }
  return latest;
}

export function latestReviewCanvas(messages: UIMessage[]): ReviewCanvasDocument | null {
  return latestReviewCanvasTarget(messages)?.document ?? null;
}
